(() => {
  'use strict';

  const config = window.LightingCentre || {};
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  let activeDrawer = null;
  let activeDrawerTrigger = null;
  let statusTimer = null;
  let cartMutationQueue = Promise.resolve();

  const announce = (message) => {
    const region = document.querySelector('[data-lc-status]');
    if (!region || !message) return;
    window.clearTimeout(statusTimer);
    region.textContent = '';
    window.requestAnimationFrame(() => {
      region.textContent = message;
      region.classList.add('is-visible');
      statusTimer = window.setTimeout(() => region.classList.remove('is-visible'), 3500);
    });
  };

  const getFocusable = (container) =>
    Array.from(container.querySelectorAll(focusableSelector)).filter(
      (element) => !element.hidden && element.getClientRects().length > 0
    );

  const setBackgroundInert = (drawer, inert) => {
    const marker = 'data-lc-drawer-inert';

    if (!inert) {
      document.querySelectorAll(`[${marker}]`).forEach((element) => {
        element.removeAttribute('inert');
        element.removeAttribute(marker);
      });
      return;
    }

    let activeBranch = drawer;
    while (activeBranch && activeBranch !== document.body) {
      const parent = activeBranch.parentElement;
      if (!parent) break;

      Array.from(parent.children).forEach((element) => {
        if (
          element === activeBranch ||
          element.contains(drawer) ||
          element.matches('script, .lc-status') ||
          element.hasAttribute('inert')
        ) {
          return;
        }

        element.setAttribute('inert', '');
        element.setAttribute(marker, '');
      });

      activeBranch = parent;
    }
  };

  const openDrawer = (drawer, trigger) => {
    if (!drawer) return;
    if (activeDrawer && activeDrawer !== drawer) closeDrawer(activeDrawer, false);

    activeDrawer = drawer;
    activeDrawerTrigger = trigger || document.activeElement;
    drawer.hidden = false;
    document.body.classList.add('lc-scroll-locked');
    setBackgroundInert(drawer, true);

    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    window.requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      const focusable = getFocusable(drawer);
      (focusable[0] || drawer).focus();
    });
  };

  const closeDrawer = (drawer = activeDrawer, restoreFocus = true) => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    setBackgroundInert(drawer, false);
    document.body.classList.remove('lc-scroll-locked');

    const replacementTrigger = drawer.id
      ? Array.from(document.querySelectorAll('[data-drawer-open]')).find(
          (candidate) => candidate.dataset.drawerOpen === drawer.id
        )
      : null;
    const trigger = activeDrawerTrigger && document.contains(activeDrawerTrigger)
      ? activeDrawerTrigger
      : replacementTrigger;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');

    window.setTimeout(() => {
      drawer.hidden = true;
      if (restoreFocus && trigger && document.contains(trigger)) trigger.focus();
    }, 250);

    activeDrawer = null;
    activeDrawerTrigger = null;
  };

  const desktopFilterMedia = window.matchMedia('(min-width: 61.875rem)');
  desktopFilterMedia.addEventListener('change', (event) => {
    if (event.matches && activeDrawer?.matches('[data-lc-filter-drawer]')) {
      closeDrawer(activeDrawer, false);
    }
  });

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      const drawer = document.getElementById(opener.dataset.drawerOpen);
      if (drawer) {
        event.preventDefault();
        openDrawer(drawer, opener);
      }
      return;
    }

    const closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      event.preventDefault();
      closeDrawer(closer.closest('[data-drawer]'));
    }
  });

  document.addEventListener('keydown', (event) => {
    if (activeDrawer) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDrawer();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = getFocusable(activeDrawer);
      if (!focusable.length) {
        event.preventDefault();
        activeDrawer.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
      return;
    }

    if (event.key === 'Escape') {
      const openMenu = event.target.closest?.('[data-mega-menu][open]') || document.querySelector('[data-mega-menu][open]');
      if (!openMenu) return;
      event.preventDefault();
      openMenu.removeAttribute('open');
      openMenu.querySelector('summary')?.focus();
    }
  });

  document.addEventListener('toggle', (event) => {
    const menu = event.target;
    if (!(menu instanceof HTMLDetailsElement) || !menu.matches('[data-mega-menu]') || !menu.open) return;
    document.querySelectorAll('[data-mega-menu][open]').forEach((other) => {
      if (other !== menu) other.removeAttribute('open');
    });
  }, true);

  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-mega-menu][open]').forEach((menu) => {
      if (!menu.contains(event.target)) menu.removeAttribute('open');
    });
  });

  const header = document.querySelector('[data-lc-header]');
  let headerFrame = null;
  let headerIsCompact = Boolean(header?.classList.contains('is-compact'));
  let compactHeaderAt = 160;
  const expandHeaderAt = 8;

  const measureExpandedHeader = () => {
    if (!header || headerIsCompact) return;
    compactHeaderAt = Math.max(160, Math.ceil(header.getBoundingClientRect().height) + 16);
  };

  const syncHeaderState = () => {
    headerFrame = null;
    if (!header) return;
    const scrollTop = Math.max(window.scrollY, document.documentElement.scrollTop || 0);
    if (!headerIsCompact && scrollTop > compactHeaderAt) headerIsCompact = true;
    else if (headerIsCompact && scrollTop <= expandHeaderAt) headerIsCompact = false;

    header.classList.toggle('is-compact', headerIsCompact);
    if (!headerIsCompact) {
      measureExpandedHeader();
      header.classList.remove('is-search-open');
      document.querySelectorAll('[data-mobile-search-toggle]').forEach((button) => {
        button.setAttribute('aria-expanded', 'true');
      });
    } else {
      const mobileSearch = header.querySelector('.lc-header__mobile-search');
      if (mobileSearch?.contains(document.activeElement)) header.classList.add('is-search-open');
      const expanded = header.classList.contains('is-search-open');
      document.querySelectorAll('[data-mobile-search-toggle]').forEach((button) => {
        button.setAttribute('aria-expanded', String(expanded));
      });
    }
  };
  measureExpandedHeader();
  syncHeaderState();
  window.addEventListener('scroll', () => {
    if (headerFrame) return;
    headerFrame = window.requestAnimationFrame(syncHeaderState);
  }, { passive: true });
  window.addEventListener('resize', () => {
    if (!headerIsCompact) measureExpandedHeader();
  }, { passive: true });

  header?.addEventListener('focusout', (event) => {
    if (!event.target.closest('.lc-header__mobile-search')) return;
    window.requestAnimationFrame(syncHeaderState);
  });

  document.addEventListener('click', (event) => {
    const searchToggle = event.target.closest('[data-mobile-search-toggle]');
    if (!searchToggle || !header) return;
    const searchRegion = document.getElementById(searchToggle.getAttribute('aria-controls'));
    if (!header.classList.contains('is-compact')) {
      searchToggle.setAttribute('aria-expanded', 'true');
      searchRegion?.querySelector('input[type="search"]')?.focus({ preventScroll: true });
      return;
    }
    const expanded = searchToggle.getAttribute('aria-expanded') !== 'true';
    header.classList.toggle('is-search-open', expanded);
    searchToggle.setAttribute('aria-expanded', String(expanded));
    if (expanded) {
      searchRegion?.querySelector('input[type="search"]')?.focus({ preventScroll: true });
    } else {
      searchToggle.focus({ preventScroll: true });
    }
  });

  class LcPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-predictive-search-input]');
      this.results = this.querySelector('[data-predictive-search-results]');
      if (!this.input || !this.results) return;

      this.abortController = null;
      this.timer = null;
      this.input.addEventListener('input', () => {
        window.clearTimeout(this.timer);
        this.timer = window.setTimeout(() => this.search(), 220);
      });
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.close();
      });
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.close();
      });
    }

    async search() {
      const term = this.input.value.trim();
      if (term.length < 2) {
        this.close();
        return;
      }

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.results.hidden = false;
      this.results.setAttribute('aria-busy', 'true');
      this.results.textContent = config.strings?.searchLoading || 'Loading';
      this.input.setAttribute('aria-expanded', 'true');

      const url = new URL(config.routes.predictiveSearch, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', 'product,collection,page,article,query');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('section_id', 'lc-predictive-search');

      try {
        const response = await fetch(url, {
          signal: this.abortController.signal,
          headers: { Accept: 'text/html' }
        });
        if (!response.ok) throw new Error(response.statusText);
        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const content = documentFragment.querySelector('[data-predictive-search-content]');
        this.results.innerHTML = content ? content.outerHTML : `<p>${config.strings?.searchEmpty || ''}</p>`;
      } catch (error) {
        if (error.name !== 'AbortError') this.results.textContent = config.strings?.searchEmpty || '';
      } finally {
        this.results.removeAttribute('aria-busy');
      }
    }

    close() {
      if (!this.results || !this.input) return;
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        this.input.focus();
        return;
      }
      if (!['ArrowDown', 'ArrowUp'].includes(event.key) || this.results.hidden) return;
      const options = getFocusable(this.results);
      if (!options.length) return;
      event.preventDefault();
      const currentIndex = options.indexOf(document.activeElement);
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex = currentIndex < 0 ? (direction > 0 ? 0 : options.length - 1) : (currentIndex + direction + options.length) % options.length;
      options[nextIndex].focus();
    }
  }

  if (!customElements.get('lc-predictive-search')) {
    customElements.define('lc-predictive-search', LcPredictiveSearch);
  }

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-sort-select]') && event.target.form) {
      event.target.form.requestSubmit();
    }
  });

  const setFacetOverflow = (group, expanded) => {
    if (!group) return;
    group.querySelectorAll('[data-facet-option][data-facet-overflow="true"]').forEach((option) => {
      const isChecked = Boolean(option.querySelector('input:checked'));
      option.classList.toggle('is-collapsed', !expanded && !isChecked);
    });
  };

  document.addEventListener('click', (event) => {
    const moreButton = event.target.closest('[data-facet-more]');
    if (!moreButton) return;

    const group = moreButton.closest('[data-facet-group]');
    const expanded = moreButton.getAttribute('aria-expanded') !== 'true';
    setFacetOverflow(group, expanded);
    moreButton.setAttribute('aria-expanded', String(expanded));
    moreButton.textContent = expanded
      ? moreButton.dataset.showLessLabel || 'Show less'
      : moreButton.dataset.showMoreLabel || 'Show more';
  });

  document.addEventListener('input', (event) => {
    const searchInput = event.target.closest('[data-facet-search]');
    if (!searchInput) return;

    const group = searchInput.closest('[data-facet-group]');
    if (!group) return;

    const query = searchInput.value.trim().toLocaleLowerCase();
    const moreButton = group.querySelector('[data-facet-more]');
    const status = group.querySelector('[data-facet-search-status]');
    const expanded = moreButton?.getAttribute('aria-expanded') === 'true';
    let visibleCount = 0;

    group.querySelectorAll('[data-facet-option]').forEach((option) => {
      const label = option.querySelector('[data-facet-option-label]')?.textContent?.trim().toLocaleLowerCase() || '';
      const isChecked = Boolean(option.querySelector('input:checked'));

      if (query) {
        option.classList.remove('is-collapsed');
        const matches = label.includes(query) || isChecked;
        option.hidden = !matches;
        if (matches) visibleCount += 1;
      } else {
        option.hidden = false;
        option.classList.toggle(
          'is-collapsed',
          option.dataset.facetOverflow === 'true' && !expanded && !isChecked
        );
      }
    });

    if (moreButton) moreButton.hidden = Boolean(query);
    if (status) {
      if (!query) {
        status.textContent = '';
      } else if (visibleCount === 0) {
        status.textContent = status.dataset.resultNone || 'No matching options';
      } else if (visibleCount === 1) {
        status.textContent = status.dataset.resultOne || '1 matching option';
      } else {
        status.textContent = (status.dataset.resultMany || '__COUNT__ matching options').replace(
          '__COUNT__',
          String(visibleCount)
        );
      }
    }
  });

  document.addEventListener('keydown', (event) => {
    const searchInput = event.target.closest('[data-facet-search]');
    if (!searchInput || event.key !== 'Enter') return;

    event.preventDefault();
    const group = searchInput.closest('[data-facet-group]');
    const firstMatch = group?.querySelector(
      '[data-facet-option]:not([hidden]):not(.is-collapsed) input:not([disabled])'
    );
    firstMatch?.focus({ preventScroll: true });
  });

  document.addEventListener('submit', (event) => {
    const facetForm = event.target.closest('[data-facet-form]');
    if (!facetForm) return;

    facetForm.querySelectorAll('[data-facet-price-input]').forEach((input) => {
      if (!input.value.trim()) input.disabled = true;
    });
  });

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-lc-favorites-open]')) document.querySelector('[data-lc-favorite-open]')?.click();
  });

  const formatMoney = (cents, currency = config.currency || 'CAD') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol'
      }).format(Number(cents || 0) / 100);
    } catch (_error) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  const enqueueCartMutation = (mutation) => {
    const queuedMutation = cartMutationQueue.then(mutation, mutation);
    cartMutationQueue = queuedMutation.catch(() => undefined);
    return queuedMutation;
  };

  const quantityMessage = (key, count, fallback) => {
    const template = config.strings?.[key] || fallback;
    return template.replace('__COUNT__', String(count));
  };

  const normalizeQuantityInput = (input, fallbackValue) => {
    if (!input) return { value: 1, adjusted: false, maximum: null };

    const minimum = Number(input.min || 0);
    const maximumAttribute = input.getAttribute('max');
    const parsedMaximum = maximumAttribute === null || maximumAttribute === ''
      ? null
      : Number(maximumAttribute);
    const maximum = Number.isFinite(parsedMaximum) ? Math.max(minimum, Math.floor(parsedMaximum)) : null;
    const parsedValue = String(input.value).trim() === '' ? Number.NaN : Number(input.value);
    const fallback = Number.isFinite(Number(fallbackValue)) ? Number(fallbackValue) : minimum;
    const wholeValue = Number.isFinite(parsedValue) ? Math.floor(parsedValue) : Math.floor(fallback);
    const value = Math.min(maximum ?? Number.POSITIVE_INFINITY, Math.max(minimum, wholeValue));
    const adjusted = !Number.isFinite(parsedValue) || parsedValue !== value;

    input.value = String(value);
    return { value, adjusted, maximum };
  };

  const getFormInventoryMaximum = (form) => {
    const quantityInput = form.querySelector('[name="quantity"]');
    const inputMaximum = quantityInput?.getAttribute('max');
    const maximumSource = inputMaximum !== null && inputMaximum !== ''
      ? inputMaximum
      : form.querySelector('[data-inventory-max]')?.value;
    if (maximumSource === undefined || maximumSource === null || maximumSource === '') return null;
    const maximum = Number(maximumSource);
    return Number.isFinite(maximum) ? Math.max(0, Math.floor(maximum)) : null;
  };

  const showProductError = (form, message) => {
    const errorRegion = form?.querySelector('[data-product-error]');
    if (errorRegion) {
      errorRegion.textContent = message || '';
      errorRegion.hidden = !message;
    }
    if (message) announce(message);
  };

  const getRemainingInventoryQuantity = (cart, variantId, inventoryMaximum) => {
    const quantityAlreadyInCart = (cart?.items || []).reduce((total, item) => {
      const itemVariantId = String(item.variant_id || item.id || '');
      return itemVariantId === String(variantId) ? total + Number(item.quantity || 0) : total;
    }, 0);
    return Math.max(0, inventoryMaximum - quantityAlreadyInCart);
  };

  const selectMedia = (root, mediaId) => {
    if (!mediaId) return;
    root.querySelectorAll('[data-media-id]').forEach((media) => {
      media.hidden = String(media.dataset.mediaId) !== String(mediaId);
    });
    root.querySelectorAll('[data-media-target]').forEach((button, index) => {
      const active = String(button.dataset.mediaTarget) === String(mediaId);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      if (active && root.querySelector('[data-media-current]')) root.querySelector('[data-media-current]').textContent = String(index + 1);
    });
  };

  const refreshPromoPrices = (root) => {
    try {
      window.LCDPromoPrices?.refresh(root);
    } catch (_error) {
      // Optional promo display must never interrupt the existing shopping behavior.
    }
  };

  const acceptPromoCart = (cart) => {
    try {
      window.LCDPromoPrices?.acceptCart(cart);
    } catch (_error) {
      // Optional promo state observation must never interrupt the existing cart flow.
    }
  };

  const setListedPriceMetadata = (element, { price, productId, variantId, productHandle, priceVaries = false, requiresSellingPlan = null, currency = config.currency || 'CAD' }) => {
    const cents = Number(price);
    if (!element || !Number.isInteger(cents) || cents < 0) return;
    element.dataset.lcdBasePrice = String(cents);
    element.dataset.lcdPriceVaries = String(priceVaries === true);
    element.dataset.lcdCurrency = currency;
    if (productId) element.dataset.lcdProductId = String(productId);
    else delete element.dataset.lcdProductId;
    if (variantId) element.dataset.lcdVariantId = String(variantId);
    else delete element.dataset.lcdVariantId;
    if (productHandle) element.dataset.lcdProductHandle = productHandle;
    else delete element.dataset.lcdProductHandle;
    if (requiresSellingPlan === 'true' || requiresSellingPlan === 'false') element.dataset.lcdRequiresSellingPlan = requiresSellingPlan;
    else delete element.dataset.lcdRequiresSellingPlan;
  };

  const renderVariantPrice = (container, variant) => {
    if (!container || !variant) return;
    container.replaceChildren();
    const wrapper = document.createElement('div');
    wrapper.className = `lc-price${variant.compare_at_price > variant.price ? ' lc-price--sale' : ''}`;
    const productRoot = container.closest('[data-product-root]');
    setListedPriceMetadata(wrapper, {
      price: variant.price,
      productId: productRoot?.dataset.lcdProductId,
      variantId: variant.id,
      productHandle: productRoot?.dataset.productHandle,
      requiresSellingPlan: productRoot?.dataset.lcdRequiresSellingPlan
    });
    const current = document.createElement('span');
    current.className = 'lc-price__current';
    const money = formatMoney(variant.price);
    money.split(/(\$)/).forEach((part) => {
      if (part === '$') {
        const symbol = document.createElement('span');
        symbol.className = 'lc-price__symbol';
        symbol.textContent = part;
        current.append(symbol);
      } else current.append(document.createTextNode(part));
    });
    wrapper.append(current);
    if (variant.compare_at_price > variant.price) {
      const compare = document.createElement('s');
      compare.className = 'lc-price__compare';
      compare.textContent = formatMoney(variant.compare_at_price);
      wrapper.append(compare);
    }
    container.append(wrapper);
  };

  const initializeRefinedGallery = (root) => {
    const hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
    root.querySelectorAll('[data-lc-image-fit]').forEach((fit) => {
      const image = fit.querySelector('img');
      const button = fit.closest('[data-media-open]');
      if (!image || !button) return;
      const values = (fit.dataset.imageBounds || '').split(',').map(Number);
      const bounds = values.length === 4 && values.every(Number.isFinite) && values[2] > 0 && values[3] > 0 && values[0] >= 0 && values[1] >= 0 && values[0] + values[2] <= 1.001 && values[1] + values[3] <= 1.001 ? values : null;
      const placeImage = () => {
        const w = button.clientWidth;
        const h = button.clientHeight;
        if (!bounds || !w || !h || !image.naturalWidth || !image.naturalHeight) return;
        const [x,y,bw,bh] = bounds;
        const scale = Math.min(w * 0.96 / (image.naturalWidth * bw), h * 0.9 / (image.naturalHeight * bh));
        const iw = image.naturalWidth * scale;
        const ih = image.naturalHeight * scale;
        Object.assign(image.style, {position:'absolute',width:`${iw}px`,height:`${ih}px`,left:`${(w-iw*bw)/2-iw*x}px`,top:`${(h-ih*bh)/2-ih*y}px`,maxWidth:'none',maxHeight:'none'});
      };
      image.addEventListener('load',placeImage);
      placeImage();
      const observer = new ResizeObserver(placeImage);
      observer.observe(button);
      const clearZoom = () => { button.classList.remove('is-zoomed'); fit.style.transformOrigin = ''; };
      button.addEventListener('pointermove',(event) => {
        if (!hoverMedia.matches || event.pointerType === 'touch') return;
        const rect = button.getBoundingClientRect();
        fit.style.transformOrigin = `${Math.max(0,Math.min(100,(event.clientX-rect.left)/rect.width*100))}% ${Math.max(0,Math.min(100,(event.clientY-rect.top)/rect.height*100))}%`;
        button.classList.add('is-zoomed');
      });
      button.addEventListener('pointerleave',clearZoom);
      button.addEventListener('click',clearZoom);
      button.addEventListener('blur',clearZoom);
      root.addEventListener('shopify:section:unload',() => observer.disconnect(),{once:true});
    });
  };


  const initializeProduct = (root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    if (root.hasAttribute('data-refined-product')) initializeRefinedGallery(root);

    const variantData = root.querySelector('[data-product-variants]');
    let variants = [];
    try {
      variants = JSON.parse(variantData?.textContent || '[]');
    } catch (_error) {
      variants = [];
    }

    const form = root.querySelector('[data-product-form]');
    const variantInput = form?.querySelector('[data-variant-id]');
    const priceContainer = root.querySelector('[data-product-price]');
    const sku = root.querySelector('[data-product-sku]');
    const skuRow = root.querySelector('[data-product-sku-row]');
    const model = root.querySelector('[data-product-model]');
    const modelRow = root.querySelector('[data-product-model-row]');
    const questionLink = root.querySelector('[data-product-question-link]');
    const availability = root.querySelector('[data-product-availability]');
    const availabilityLabel = availability?.querySelector('[data-availability-label]');
    const availabilityQuantity = availability?.querySelector('[data-availability-quantity]');
    const addButton = root.querySelector('[data-add-to-cart]');
    const addText = root.querySelector('[data-add-to-cart-text]');
    const quantityInput = root.querySelector('[data-quantity-input]');
    const quantityButtons = root.querySelectorAll('[data-quantity-increase], [data-quantity-decrease]');
    const dynamicCheckout = root.querySelector('[data-dynamic-checkout]');
    const sticky = root.querySelector('[data-sticky-purchase]');
    const stickyPrice = root.querySelector('[data-sticky-price]');
    const stickyButton = root.querySelector('[data-sticky-submit]');
    const productError = form?.querySelector('[data-product-error]');
    const requiresConfirmation = root.dataset.requiresConfirmation === 'true';
    const productGrid = root.querySelector('.lc-product__grid');
    const productInfo = root.querySelector('.lc-product-info');
    const productSummary = root.querySelector('.lc-product-summary');
    const productBuying = root.querySelector('.lc-product-buying');
    const productGallery = root.querySelector('.lc-product-gallery');
    const productMediaSkip = root.querySelector('[data-product-media-skip]');
    const mobileProductLayout = window.matchMedia('(max-width: 46.8125rem)');

    const syncProductLayout = () => {
      if (!productGrid || !productInfo || !productSummary || !productBuying || !productGallery) return;
      if (mobileProductLayout.matches) {
        if (productSummary.parentElement !== productGrid) productGrid.insertBefore(productSummary, productGallery);
        if (productMediaSkip) productMediaSkip.href = `#${productBuying.id}`;
      } else if (productSummary.parentElement !== productInfo) {
        productInfo.insertBefore(productSummary, productBuying);
        if (productMediaSkip) productMediaSkip.href = `#${productSummary.id}`;
      } else if (productMediaSkip) {
        productMediaSkip.href = `#${productSummary.id}`;
      }
    };

    syncProductLayout();
    if (typeof mobileProductLayout.addEventListener === 'function') {
      mobileProductLayout.addEventListener('change', syncProductLayout);
    } else {
      mobileProductLayout.addListener(syncProductLayout);
    }

    const syncQuantityButtons = () => {
      if (!quantityInput) return;
      const value = Number(quantityInput.value || quantityInput.min || 1);
      const minimum = Number(quantityInput.min || 1);
      const maximumAttribute = quantityInput.getAttribute('max');
      const maximum = maximumAttribute === null || maximumAttribute === ''
        ? null
        : Number(maximumAttribute);
      const quantityDisabled = quantityInput.disabled;
      root.querySelectorAll('[data-quantity-decrease]').forEach((button) => {
        button.disabled = quantityDisabled || value <= minimum;
      });
      root.querySelectorAll('[data-quantity-increase]').forEach((button) => {
        button.disabled = quantityDisabled || (Number.isFinite(maximum) && value >= maximum);
      });
    };

    const getInventoryState = (variant) => {
      const quantity = Number(variant?.inventory_quantity || 0);
      const tracked = Boolean(variant?.inventory_management);
      if (!variant?.available) {
        return { code: 'SOLD_OUT', label: config.strings?.soldOut || 'Sold out', quantityLabel: '', canPurchase: false };
      }
      if (requiresConfirmation) {
        return {
          code: 'CALL_TO_CONFIRM',
          label: config.strings?.contactToConfirm || 'Contact us to confirm',
          quantityLabel: '',
          canPurchase: false
        };
      }
      if (tracked && quantity > 0) {
        const template = quantity === 1
          ? (config.strings?.quantityOne || '1 available')
          : quantity <= 3
            ? (config.strings?.quantityLow || 'Only __COUNT__ available')
            : (config.strings?.quantityMany || '__COUNT__ available');
        return {
          code: 'IN_STOCK',
          label: config.strings?.inStock || 'In stock',
          quantityLabel: template.replace('__COUNT__', String(quantity)),
          canPurchase: true,
          max: variant.inventory_policy === 'deny' ? quantity : null
        };
      }
      if (tracked && variant.inventory_policy === 'continue') {
        return {
          code: 'AVAILABLE_TO_ORDER',
          label: config.strings?.availableToOrder || 'Available to order',
          quantityLabel: '',
          canPurchase: true
        };
      }
      return {
        code: 'AVAILABLE',
        label: config.strings?.available || 'Available',
        quantityLabel: '',
        canPurchase: true
      };
    };

    const updateQuantityState = (inventoryState) => {
      if (!quantityInput) return;
      const previousValue = Number(quantityInput.value || 1);
      quantityInput.disabled = !inventoryState.canPurchase;
      quantityButtons.forEach((button) => {
        button.disabled = !inventoryState.canPurchase;
      });
      if (inventoryState.max) {
        quantityInput.max = String(inventoryState.max);
        quantityInput.value = String(Math.min(Math.max(Number(quantityInput.value || 1), 1), inventoryState.max));
      } else {
        quantityInput.removeAttribute('max');
        if (Number(quantityInput.value || 0) < 1) quantityInput.value = '1';
      }
      syncQuantityButtons();
      if (inventoryState.canPurchase && Number(quantityInput.value) !== previousValue && inventoryState.max) {
        const message = quantityMessage(
          'quantityAdjusted',
          inventoryState.max,
          'Only __COUNT__ can be purchased. The quantity has been adjusted.'
        );
        if (productError) {
          productError.textContent = message;
          productError.hidden = false;
        }
        announce(message);
      }
    };

    const syncProductQuestionLink = (variant) => {
      if (!questionLink) return;

      const target = new URL(questionLink.getAttribute('href') || questionLink.href, window.location.origin);
      const values = {
        product: root.dataset.productTitle || '',
        sku: variant?.sku || '',
        model: variant?.manufacturer_model || '',
        purl: root.dataset.productCanonicalUrl || ''
      };

      Object.entries(values).forEach(([key, value]) => {
        if (value) {
          target.searchParams.set(key, value);
        } else {
          target.searchParams.delete(key);
        }
      });

      questionLink.href = target.toString();
    };

    const syncAvailability = (inventoryState) => {
      if (!availability) return;

      const stateClasses = [
        'lc-availability--available',
        'lc-availability--in-stock',
        'lc-availability--in_stock',
        'lc-availability--available-to-order',
        'lc-availability--available_to_order',
        'lc-availability--sold-out',
        'lc-availability--sold_out',
        'lc-availability--call-to-confirm',
        'lc-availability--call_to_confirm'
      ];
      const stateClass = `lc-availability--${String(inventoryState.code || 'AVAILABLE').toLowerCase().replace(/_/g, '-')}`;

      availability.dataset.availabilityCode = inventoryState.code || 'AVAILABLE';
      availability.classList.remove(...stateClasses);
      availability.classList.add(stateClass);
      availability.classList.toggle('is-unavailable', !inventoryState.canPurchase);
      if (availabilityLabel) availabilityLabel.textContent = inventoryState.label || '';
      if (availabilityQuantity) {
        availabilityQuantity.textContent = inventoryState.quantityLabel || '';
        availabilityQuantity.hidden = !inventoryState.quantityLabel;
      }
    };

    let detailRequest;
    let detailRequestNumber = 0;
    const detailHost = root.querySelector('[data-refined-specifications]');
    const detailContent = root.querySelector('[data-refined-specification-content]');
    const detailStatus = root.querySelector('[data-refined-details-status]');
    const detailFailure = root.querySelector('[data-refined-details-failure]');
    const finishSummary = root.querySelector('[data-refined-finish]');
    const updateVariantDetails = async (variant) => {
      if (!detailHost || !detailContent) return;
      detailRequest?.abort();
      const requestNumber = ++detailRequestNumber;
      if (variant?.display_title) root.querySelectorAll('[data-product-display-title]').forEach((title) => { title.textContent = variant.display_title; });
      if (finishSummary) {
        finishSummary.textContent = variant?.display_finish || '';
        finishSummary.hidden = !variant?.display_finish;
      }
      detailContent.hidden = true;
      if (detailFailure) detailFailure.hidden = true;
      if (!variant) {
        detailHost.removeAttribute('aria-busy');
        if (detailStatus) detailStatus.hidden = true;
        return;
      }
      detailHost.setAttribute('aria-busy', 'true');
      if (detailStatus) detailStatus.hidden = false;
      detailRequest = new AbortController();
      const detailUrl = new URL(window.location.href);
      detailUrl.searchParams.set('variant', variant.id);
      detailUrl.searchParams.set('section_id', root.dataset.sectionId);
      try {
        const response = await fetch(detailUrl, { signal: detailRequest.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Specifications unavailable');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = parsed.querySelector('[data-refined-specification-content]');
        if (!next || next.dataset.refinedVariant !== String(variant.id)) throw new Error('Variant details mismatch');
        if (requestNumber !== detailRequestNumber || !root.isConnected) return;
        detailContent.innerHTML = next.innerHTML;
        detailContent.dataset.refinedVariant = next.dataset.refinedVariant;
        detailContent.hidden = false;
      } catch (error) {
        if (error.name === 'AbortError' || requestNumber !== detailRequestNumber) return;
        if (detailFailure) {
          const retry = detailFailure.querySelector('a');
          if (retry) {
            detailUrl.searchParams.delete('section_id');
            retry.href = detailUrl.toString();
          }
          detailFailure.hidden = false;
        }
      } finally {
        if (requestNumber === detailRequestNumber) {
          detailHost.removeAttribute('aria-busy');
          if (detailStatus) detailStatus.hidden = true;
        }
      }
    };

    const updateVariant = () => {
      const selections = Array.from(root.querySelectorAll('[data-product-option]:checked'))
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((input) => input.value);
      if (!selections.length) return;

      const variant = variants.find(
        (candidate) => candidate.options.length === selections.length && candidate.options.every((option, index) => option === selections[index])
      );

      root.querySelectorAll('[data-option-value]').forEach((label) => {
        const checked = root.querySelector(`[data-product-option][data-option-position="${label.dataset.optionValue}"]:checked`);
        if (checked) label.textContent = checked.value;
      });

      if (!variant) {
        if (variantInput) variantInput.value = '';
        updateVariantDetails(null);
        if (sku) sku.textContent = '';
        if (skuRow) skuRow.hidden = true;
        if (model) model.textContent = '';
        if (modelRow) modelRow.hidden = true;
        syncProductQuestionLink(null);
        if (addButton) addButton.disabled = true;
        if (stickyButton) stickyButton.disabled = true;
        if (dynamicCheckout) dynamicCheckout.hidden = true;
        updateQuantityState({ canPurchase: false });
        if (addText) addText.textContent = config.strings?.soldOut || 'Unavailable';
        syncAvailability({
          code: 'SOLD_OUT',
          label: config.strings?.soldOut || 'Unavailable',
          quantityLabel: '',
          canPurchase: false
        });
        return;
      }

      if (variantInput) {
        variantInput.value = variant.id;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (sku) sku.textContent = variant.sku || '';
      if (skuRow) skuRow.hidden = !variant.sku;
      const manufacturerModel = variant.manufacturer_model || '';
      if (model) model.textContent = manufacturerModel;
      if (modelRow) modelRow.hidden = !manufacturerModel;
      syncProductQuestionLink(variant);
      updateVariantDetails(variant);
      renderVariantPrice(priceContainer, variant);
      if (stickyPrice) {
        setListedPriceMetadata(stickyPrice, {
          price: variant.price,
          productId: root.dataset.lcdProductId,
          variantId: variant.id,
          productHandle: root.dataset.productHandle,
          requiresSellingPlan: root.dataset.lcdRequiresSellingPlan
        });
        stickyPrice.textContent = formatMoney(variant.price);
      }

      const inventoryState = getInventoryState(variant);
      syncAvailability(inventoryState);
      const canPurchase = inventoryState.canPurchase;
      if (addButton) addButton.disabled = !canPurchase;
      if (dynamicCheckout) dynamicCheckout.hidden = !canPurchase;
      updateQuantityState(inventoryState);
      if (stickyButton) {
        stickyButton.disabled = !canPurchase;
        stickyButton.textContent = variant.available
          ? (canPurchase ? (config.strings?.addToCart || 'Add to cart') : (config.strings?.contactToConfirm || 'Contact us to confirm'))
          : (config.strings?.soldOut || 'Sold out');
      }
      if (addText) {
        addText.textContent = variant.available
          ? (canPurchase ? (config.strings?.addToCart || 'Add to cart') : (config.strings?.contactToConfirm || 'Contact us to confirm'))
          : (config.strings?.soldOut || 'Sold out');
      }

      const featuredMediaId = variant.featured_media?.id || variant.featured_media_id;
      if (featuredMediaId) selectMedia(root, featuredMediaId);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      refreshPromoPrices(root);
    };

    root.querySelectorAll('[data-product-option]').forEach((input) => input.addEventListener('change', updateVariant));

    root.querySelectorAll('[data-media-target]').forEach((button) => {
      button.addEventListener('click', () => selectMedia(root, button.dataset.mediaTarget));
    });

    root.querySelectorAll('[data-quantity]').forEach((quantity) => {
      const input = quantity.querySelector('input[type="number"]');
      quantity.querySelector('[data-quantity-decrease]')?.addEventListener('click', () => {
        input.value = Math.max(Number(input.min || 1), Number(input.value || 1) - 1);
        syncQuantityButtons();
      });
      quantity.querySelector('[data-quantity-increase]')?.addEventListener('click', () => {
        const nextValue = Number(input.value || 1) + 1;
        input.value = input.max ? Math.min(Number(input.max), nextValue) : nextValue;
        syncQuantityButtons();
      });
      input.addEventListener('input', syncQuantityButtons);
      input.addEventListener('change', () => {
        const normalized = normalizeQuantityInput(input, 1);
        syncQuantityButtons();
        if (normalized.adjusted) {
          const count = normalized.maximum ?? normalized.value;
          const message = quantityMessage(
            'quantityAdjusted',
            count,
            'Only __COUNT__ can be purchased. The quantity has been adjusted.'
          );
          showProductError(form, message);
          input.focus();
        } else if (productError) {
          productError.textContent = '';
          productError.hidden = true;
        }
      });
    });
    syncQuantityButtons();

    const dialog = root.querySelector('[data-media-dialog]');
    const dialogContent = root.querySelector('[data-media-dialog-content]');
    let dialogTrigger = null;
    root.querySelectorAll('[data-media-open]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!dialog || !dialogContent) return;
        const image = button.querySelector('img');
        if (!image) return;
        const clone = image.cloneNode(true);
        clone.removeAttribute('sizes');
        clone.removeAttribute('style');
        dialogContent.replaceChildren(clone);
        dialogTrigger = button;
        dialog.showModal();
        dialog.querySelector('[data-media-close]')?.focus();
      });
    });
    root.querySelector('[data-media-close]')?.addEventListener('click', () => dialog?.close());
    dialog?.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      dialog.close();
    });
    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog?.addEventListener('close', () => {
      dialogContent?.replaceChildren();
      if (dialogTrigger && document.contains(dialogTrigger)) dialogTrigger.focus();
      dialogTrigger = null;
    });

    if (sticky && addButton) {
      let buttonVisible = true;
      const updateSticky = () => {
        const passedButton = window.scrollY > addButton.getBoundingClientRect().top + window.scrollY;
        sticky.hidden = buttonVisible || !passedButton;
      };
      new IntersectionObserver((entries) => {
        buttonVisible = entries[0].isIntersecting;
        updateSticky();
      }, { threshold: 0.1 }).observe(addButton);
      window.addEventListener('scroll', updateSticky, { passive: true });
      stickyButton?.addEventListener('click', () => form?.requestSubmit(addButton));
    }
  };

  document.querySelectorAll('[data-product-root]').forEach(initializeProduct);

  const replaceCartDrawer = (sections) => {
    const html = sections?.['lc-cart-drawer'];
    if (!html) return null;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const replacement = parsed.querySelector('[data-cart-drawer-section]');
    const current = document.querySelector('[data-cart-drawer-section]');
    if (!replacement || !current) return null;
    current.replaceWith(replacement);
    return replacement;
  };

  const refreshCartCount = async () => {
    const response = await fetch(`${config.routes.cart}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    acceptPromoCart(cart);
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
      count.setAttribute('aria-label', `${cart.item_count}`);
    });
  };

  const validateAddQuantityAgainstCart = async (formData, inventoryMaximum) => {
    if (inventoryMaximum === null) return;

    let response;
    try {
      response = await fetch(`${config.routes.cart}.js`, { headers: { Accept: 'application/json' } });
    } catch (_error) {
      throw new Error(config.strings?.cartError || 'We could not update your cart. Please try again.');
    }
    if (!response.ok) {
      throw new Error(config.strings?.cartError || 'We could not update your cart. Please try again.');
    }

    let cart;
    try {
      cart = await response.json();
    } catch (_error) {
      throw new Error(config.strings?.cartError || 'We could not update your cart. Please try again.');
    }
    const variantId = String(formData.get('id') || '');
    const requestedQuantity = Number(formData.get('quantity') || 1);
    const remainingQuantity = getRemainingInventoryQuantity(cart, variantId, inventoryMaximum);

    if (requestedQuantity > remainingQuantity) {
      throw new Error(
        quantityMessage(
          'addQuantityExceeded',
          remainingQuantity,
          'You can add up to __COUNT__ more of this item.'
        )
      );
    }
  };

  const submitProductForm = (form, submitter) => {
    if (form.dataset.cartMutationPending === 'true') return Promise.resolve();

    const button = submitter || form.querySelector('[type="submit"]');
    const errorRegion = form.querySelector('[data-product-error]');
    const quantityInput = form.querySelector('[name="quantity"]');
    const normalizedQuantity = normalizeQuantityInput(quantityInput, 1);
    if (normalizedQuantity.adjusted) {
      const count = normalizedQuantity.maximum ?? normalizedQuantity.value;
      const message = quantityMessage(
        'quantityAdjusted',
        count,
        'Only __COUNT__ can be purchased. The quantity has been adjusted.'
      );
      showProductError(form, message);
      quantityInput?.focus();
      return Promise.resolve();
    }

    const formData = new FormData(form);
    formData.set('sections', 'lc-cart-drawer');
    formData.set('sections_url', window.location.pathname);
    const inventoryMaximum = getFormInventoryMaximum(form);
    form.dataset.cartMutationPending = 'true';
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    if (errorRegion) {
      errorRegion.hidden = true;
      errorRegion.textContent = '';
    }

    return enqueueCartMutation(async () => {
      try {
        await validateAddQuantityAgainstCart(formData, inventoryMaximum);
        const response = await fetch(`${config.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || config.strings?.cartError);
        const drawer = replaceCartDrawer(result.sections);
        await refreshCartCount();
        announce(config.strings?.cartUpdated);
        const drawerTrigger = button?.closest('[data-lc-favorite-drawer]')
          ? document.querySelector('[data-lc-favorite-open]')
          : button;
        if (drawer) openDrawer(drawer, drawerTrigger);
      } catch (error) {
        showProductError(form, error.message || config.strings?.cartError);
      } finally {
        delete form.dataset.cartMutationPending;
        if (button && document.contains(button)) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }
    });
  };

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-product-form], [data-quick-add]');
    if (!form) return;
    if (form.matches('[data-product-form]') && event.submitter && !event.submitter.matches('[data-add-to-cart]')) return;
    event.preventDefault();
    submitProductForm(form, event.submitter);
}, true);

  const refreshCartDrawer = async () => {
    try {
      const url = new URL(config.routes.cart || '/cart', window.location.origin);
      url.searchParams.set('sections', 'lc-cart-drawer');
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      return replaceCartDrawer(await response.json());
    } catch (_error) {
      return null;
    }
  };

  const revealReplacementDrawer = (drawer) => {
    if (!drawer) return;
    drawer.hidden = false;
    drawer.classList.add('is-open');
    activeDrawer = drawer;
  };

  const setDrawerCheckoutDisabled = (drawer, disabled) => {
    drawer?.querySelectorAll('[data-cart-checkout]').forEach((button) => {
      button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
    });
  };

  const changeCartLine = (identifier, quantity, trigger, useLineNumber = false) => {
    if (trigger?.dataset.cartMutationPending === 'true') return Promise.resolve();

    const previousQuantity = Number(trigger?.dataset.cartPreviousQuantity || trigger?.value || 0);
    if (trigger) trigger.dataset.cartMutationPending = 'true';
    if (trigger) {
      trigger.disabled = true;
      trigger.setAttribute('aria-busy', 'true');
    }
    const drawerWasOpen = Boolean(trigger?.closest('[data-cart-drawer-section]')?.classList.contains('is-open'));
    setDrawerCheckoutDisabled(trigger?.closest('[data-cart-drawer-section]'), true);

    return enqueueCartMutation(async () => {
      try {
        if (trigger && !document.contains(trigger)) {
          announce(config.strings?.cartUpdated);
          return;
        }

        const requestBody = {
          quantity: Number(quantity),
          sections: ['lc-cart-drawer'],
          sections_url: window.location.pathname
        };
        if (useLineNumber) {
          requestBody.line = Number(identifier);
        } else {
          requestBody.id = String(identifier);
        }

        const response = await fetch(`${config.routes.cartChange}.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });
        const cart = await response.json();
        if (!response.ok) throw new Error(cart.description || cart.message || config.strings?.cartError);
        if (cart?.item_count !== undefined) acceptPromoCart(cart);
        const drawer = replaceCartDrawer(cart.sections);
        document.querySelectorAll('[data-cart-count]').forEach((count) => {
          count.textContent = cart.item_count;
          count.setAttribute('aria-label', `${cart.item_count}`);
        });
        announce(config.strings?.cartUpdated);
        revealReplacementDrawer(drawer);
      } catch (error) {
        if (trigger && document.contains(trigger)) trigger.value = String(previousQuantity);
        const message = error.message || config.strings?.cartError;
        announce(message);
        const drawer = await refreshCartDrawer();
        const drawerError = drawer?.querySelector('[data-cart-drawer-error]');
        if (drawerError) {
          drawerError.textContent = message;
          drawerError.hidden = false;
        }
        if (drawerWasOpen) revealReplacementDrawer(drawer);
      } finally {
        if (trigger) delete trigger.dataset.cartMutationPending;
        if (trigger && document.contains(trigger)) {
          trigger.disabled = false;
          trigger.removeAttribute('aria-busy');
        }
      }
    });
  };

  document.addEventListener('change', (event) => {
    const quantity = event.target.closest('[data-cart-quantity]');
    if (!quantity) return;

    const normalized = normalizeQuantityInput(quantity, quantity.dataset.cartPreviousQuantity);
    if (normalized.adjusted) {
      const count = normalized.maximum ?? normalized.value;
      announce(
        quantityMessage(
          'quantityAdjusted',
          count,
          'Only __COUNT__ can be purchased. The quantity has been adjusted.'
        )
      );
    }
    changeCartLine(
      quantity.dataset.cartKey || quantity.dataset.line,
      normalized.value,
      quantity,
      !quantity.dataset.cartKey
    );
  });

  document.addEventListener('input', (event) => {
    const quantity = event.target.closest('[data-cart-quantity]');
    if (!quantity) return;
    setDrawerCheckoutDisabled(
      quantity.closest('[data-cart-drawer-section]'),
      !quantity.checkValidity()
    );
  });

  document.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-cart-remove]');
    if (!remove) return;
    event.preventDefault();
    changeCartLine(remove.dataset.cartKey || remove.dataset.line, 0, remove, !remove.dataset.cartKey);
  });

  const updateCartPageCheckoutState = (form) => {
    const quantityInputs = Array.from(form.querySelectorAll('[data-cart-page-quantity]'));
    const hasInvalidQuantity = quantityInputs.some((input) => !input.checkValidity());
    form.querySelectorAll('[data-cart-checkout]').forEach((button) => {
      button.disabled = hasInvalidQuantity;
      button.setAttribute('aria-disabled', String(hasInvalidQuantity));
    });
    return !hasInvalidQuantity;
  };

  const showCartPageError = (form, message) => {
    const errorRegion = form?.querySelector('[data-cart-page-error]');
    if (!errorRegion) return;
    errorRegion.textContent = message || '';
    errorRegion.hidden = !message;
  };

  document.addEventListener('input', (event) => {
    const quantity = event.target.closest('[data-cart-page-quantity]');
    if (!quantity) return;
    updateCartPageCheckoutState(quantity.form);
  });

  document.addEventListener('change', (event) => {
    const quantity = event.target.closest('[data-cart-page-quantity]');
    if (!quantity) return;

    const normalized = normalizeQuantityInput(quantity, quantity.dataset.cartPreviousQuantity);
    quantity.dataset.cartPreviousQuantity = String(normalized.value);
    const form = quantity.form;
    if (normalized.adjusted) {
      const count = normalized.maximum ?? normalized.value;
      const message = quantityMessage(
        'quantityAdjusted',
        count,
        'Only __COUNT__ can be purchased. The quantity has been adjusted.'
      );
      showCartPageError(form, message);
      announce(message);
      quantity.focus();
    } else if (updateCartPageCheckoutState(form)) {
      showCartPageError(form, '');
    }
    updateCartPageCheckoutState(form);
  });

  document.addEventListener('invalid', (event) => {
    const quantity = event.target.closest('[data-cart-page-quantity]');
    if (!quantity) return;
    const message = config.strings?.quantityReview || 'Available stock has changed. Review item quantities before checkout.';
    showCartPageError(quantity.form, message);
    announce(message);
  }, true);

  class LcProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.dataset.loaded === 'true') return;
      const observer = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        this.load();
      }, { rootMargin: '300px 0px' });
      observer.observe(this);
    }

    async load() {
      this.dataset.loaded = 'true';
      try {
        const response = await fetch(this.dataset.url);
        if (!response.ok) return;
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const replacement = parsed.querySelector('product-recommendations');
        if (replacement?.innerHTML.trim()) this.innerHTML = replacement.innerHTML;
      } catch (_error) {
        // Recommendations are optional; the core page remains usable.
      }
    }
  }

  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', LcProductRecommendations);
  }

  const buildRecentCard = (product, section) => {
    const article = document.createElement('article');
    article.className = 'lc-product-card';

    const media = document.createElement('div');
    media.className = 'lc-product-card__media';
    const imageLink = document.createElement('a');
    imageLink.className = 'lc-product-card__image-link';
    imageLink.href = product.url || `${config.routes.root}products/${product.handle}`;
    imageLink.setAttribute('aria-label', product.title);
    if (product.featured_image) {
      const image = document.createElement('img');
      image.className = 'lc-product-card__image lc-product-card__image--primary';
      image.src = `${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=640`;
      image.alt = product.title;
      image.loading = 'lazy';
      image.width = 640;
      image.height = 720;
      imageLink.append(image);
    }
    media.append(imageLink);

    const content = document.createElement('div');
    content.className = 'lc-product-card__content';
    if (product.vendor) {
      const vendor = document.createElement('p');
      vendor.className = 'lc-product-card__vendor';
      vendor.textContent = product.vendor;
      content.append(vendor);
    }

    const heading = document.createElement('h3');
    heading.className = 'lc-product-card__title';
    const titleLink = document.createElement('a');
    titleLink.href = imageLink.href;
    titleLink.textContent = product.title;
    heading.append(titleLink);
    content.append(heading);

    const price = document.createElement('div');
    price.className = 'lc-price';
    const listedVariants = Array.isArray(product.variants) ? product.variants : [];
    const listedVariant = product.price_varies === true ? null : (listedVariants.find((variant) => variant.available) || listedVariants[0]);
    setListedPriceMetadata(price, {
      price: product.price,
      productId: product.id,
      variantId: listedVariant && Number(listedVariant.price) === Number(product.price) ? listedVariant.id : null,
      productHandle: product.handle,
      priceVaries: product.price_varies === true,
      currency: section.dataset.currency || config.currency || 'CAD'
    });
    const current = document.createElement('span');
    current.className = 'lc-price__current';
    current.textContent = formatMoney(product.price, section.dataset.currency);
    price.append(current);
    content.append(price);

    if (!product.available) {
      const unavailable = document.createElement('p');
      unavailable.className = 'lc-product-card__finish';
      unavailable.textContent = section.dataset.soldOutLabel;
      content.append(unavailable);
    }

    const action = document.createElement('div');
    action.className = 'lc-product-card__action';
    const view = document.createElement('a');
    view.className = 'lc-button lc-button--secondary lc-button--full';
    view.href = imageLink.href;
    view.textContent = section.dataset.viewLabel;
    action.append(view);
    content.append(action);

    article.append(media, content);
    return article;
  };

  const initializeRecentlyViewed = async (section) => {
    const storageKey = 'lc-recent-products';
    const currentHandle = section.dataset.currentHandle;
    let handles = [];
    try {
      handles = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
    } catch (_error) {
      handles = [];
    }

    if (currentHandle) {
      handles = [currentHandle, ...handles.filter((handle) => handle !== currentHandle)].slice(0, 12);
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(handles));
      } catch (_error) {
        // Recently viewed is optional and never blocks shopping.
      }
    }

    const visibleHandles = handles.filter((handle) => handle !== currentHandle).slice(0, Number(section.dataset.limit || 4));
    if (!visibleHandles.length) return;

    const products = await Promise.all(
      visibleHandles.map(async (handle) => {
        try {
          const response = await fetch(`${config.routes.root}products/${encodeURIComponent(handle)}.js`);
          return response.ok ? response.json() : null;
        } catch (_error) {
          return null;
        }
      })
    );

    const grid = section.querySelector('[data-recently-viewed-grid]');
    const validProducts = products.filter(Boolean);
    if (!grid || !validProducts.length) return;
    validProducts.forEach((product) => grid.append(buildRecentCard(product, section)));
    section.hidden = false;
    refreshPromoPrices(grid);
  };

  document.querySelectorAll('[data-recently-viewed]').forEach(initializeRecentlyViewed);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-product-root]').forEach(initializeProduct);
    event.target.querySelectorAll('[data-recently-viewed]').forEach(initializeRecentlyViewed);
  });
})();


(() => {
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-brand-pause]');
    if (!button) return;
    const paused = button.getAttribute('aria-pressed') !== 'true';
    button.setAttribute('aria-pressed', String(paused));
    button.closest('[data-brand-signatures]').dataset.paused = String(paused);
  });
})();
